import { create } from "zustand";
import { PDA1090_R01Dto, StockInfo } from "./PDA1090_R01Dto";

/**
 * PDA1090_R01 재고실사 등록 Store
 * 
 * - LOT 스캔으로 품목/재고 정보 조회
 * - 창고 선택 후 실재고 입력
 * - 재고 조정 처리
 */
export interface PDA1090_R01Store {
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // 입력 상태
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  lotNo: string;                      // LOT 번호 입력
  actualStock: number | null;         // 실재고 입력

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // 조회 데이터
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  data: PDA1090_R01Dto | null;        // 조회된 데이터
  stockList: StockInfo[];             // 창고별 재고 목록

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // 선택 상태
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  selectedStock: StockInfo | null;    // 선택된 창고

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // 로딩 상태
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  loading: boolean;

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Actions
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  setLotNo: (lotNo: string) => void;
  setActualStock: (qty: number | null) => void;
  setData: (data: PDA1090_R01Dto | null) => void;
  setStockList: (list: StockInfo[]) => void;
  setSelectedStock: (stock: StockInfo | null) => void;
  setLoading: (loading: boolean) => void;

  // 초기화
  reset: () => void;
  resetForm: () => void;
}

const usePDA1090_R01Store = create<PDA1090_R01Store>((set) => ({
  // 초기값
  lotNo: "",
  actualStock: null,
  data: null,
  stockList: [],
  selectedStock: null,
  loading: false,

  // Actions
  setLotNo: (lotNo) => set({ lotNo }),
  setActualStock: (qty) => set({ actualStock: qty }),
  setData: (data) => set({
    data,
    stockList: data?.stockList ?? [],
    // 스캔한 LOT는 Info 전용 → 저장 대상은 항상 스캔 LOT(dto) 기준
    selectedStock: data
      ? {
          warehouseCd: data.warehouseCd,
          warehouseNm: data.warehouseNm,
          itemStatus: data.itemStatus,
          stockQty: data.currentStock,
          locationCd: data.locationCd,
        }
      : null,
  }),
  setStockList: (list) => set({ stockList: list }),
  setSelectedStock: (stock) => set({ selectedStock: stock }),
  setLoading: (loading) => set({ loading }),

  // 전체 초기화
  reset: () =>
    set({
      lotNo: "",
      actualStock: null,
      data: null,
      stockList: [],
      selectedStock: null,
      loading: false,
    }),

  // 폼만 초기화 (LOT 입력 + 실재고)
  resetForm: () =>
    set({
      lotNo: "",
      actualStock: null,
    }),
}));

export default usePDA1090_R01Store;
